import { ACTION_TYPE } from "../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../core/redux/api/circuitBreaker/module";
import { apiGetEditableEffectiveDateRangeAccessRules } from "../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { assoc, isNilOrEmpty } from "../language/module";
import { getResource } from "../language/resources/getResource";
import * as R from "ramda";
import { EditableEffectiveDateRangeAccessRule } from "./models";

export const KEY_ACTION =
  ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
  ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_RESPONSE,
  ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_ERROR,
];

/**
 * An action to dispatch to get the list of editable effective date range access rules for the specified resort
 */
export const getEditableEffectiveDateRangeRulesAction = (
  resortAbbreviation: string,
) =>
  makeApiAction({
    endpoint: apiGetEditableEffectiveDateRangeAccessRules(resortAbbreviation),
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { resortAbbreviation },
    })) as RsaaTypes,
    // Bail if already loading results for the resort or if the circuit breaker has tripped for that query
    bailout: (state: IAppState) =>
      hasTrippedBreaker(state, KEY_ACTION) ||
      !!getLoading(state, resortAbbreviation, KEY_ACTION),
  });

const reducer: ApiReducer<EditableEffectiveDateRangeAccessRule[]> = (
  state,
  action,
  error,
  isLoading,
  payload,
) => {
  const resort = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(
    ["selectedResort", "resortAbbreviation"],
    state,
  );

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts
  if (
    action.type ===
      ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_RESPONSE &&
    !isNilOrEmpty(appStateResort) &&
    appStateResort !== resort
  ) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if (isLoading) {
    const withLoading = addLoading(state, resort, KEY_ACTION);

    // Clear old data when a new request starts
    return assoc("editableEffectiveDateRangeAccessRules", [], withLoading);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      resort,
      KEY_ACTION,
      error ??
        getResource(
          state.locale,
          "GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_ACTION_ERROR",
        ),
    );
  }

  if (!payload) {
    payload = [];
  }

  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    resort,
    KEY_ACTION,
  );

  return assoc(
    "editableEffectiveDateRangeAccessRules",
    payload,
    noLoadingOrError,
  );
};

export const getEditableEffectiveDateRangeRulesReducer = makeApiReducer<
  EditableEffectiveDateRangeAccessRule[]
>(ACTIONS, KEY_ACTION, reducer);
